import styled from 'styled-components';
import { ContentSection } from '../CompensationManagement/CompensationManagement.styles';

/* ════════════════════════════════════════════════════════════════════════════
   CONTENT SECTION — forces the same side padding as CompensationManagement's
   base ContentSection (!important since the base rule wasn't visibly taking
   effect), so Single Update's table and Bulk Update's toolbar aren't flush
   against the page edge.
   ════════════════════════════════════════════════════════════════════════════ */

export const RoleReportingContentSectionStyled = styled(ContentSection)`
  padding: 0 24px !important;
`;

/* ════════════════════════════════════════════════════════════════════════════
   HEADER — page title and tabs sized/weighted to match Upload External JD
   (AdminSetupUploadJd.styles.js's Typography.Title level={3} and TabsRow).
   ════════════════════════════════════════════════════════════════════════════ */

export const RoleReportingTabsRowStyled = styled.div`
  padding: 0 24px;
  .ant-tabs-tab .ant-tabs-tab-btn {
    font-size: 14px;
    font-weight: 500;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   TABLE WRAPPER — same structure as PAICTableWrapperStyled (PeoplePlusAIV2)
   so pagination stays pinned to the bottom of the scrollable table area
   ════════════════════════════════════════════════════════════════════════════ */

export const RoleReportingSectionStyled = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  position: relative;
`;

export const RoleReportingTableWrapperStyled = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  .ant-table-tbody > tr.rr-editing-row > td {
    background: ${({ theme }) => theme.colors.warningBg};
  }

  .ant-table-pagination {
    position: sticky;
    bottom: 0;
    background: ${({ theme }) => theme.colors.white};
    padding: 12px 16px;
    margin: 0 !important;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   STATUS / APEX BADGES
   ════════════════════════════════════════════════════════════════════════════ */

export const StatusBadgeStyled = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
  color: ${({ $status, theme }) => {
    if ($status === 'PUBLISHED') return theme.colors.statusCompleted;
    if ($status === 'PENDING_APPROVAL') return theme.colors.warningMessage;
    if ($status === 'UNPUBLISHED') return theme.colors.tertiary;
    return theme.colors.info;
  }};
  background: ${({ $status, theme }) => {
    if ($status === 'PUBLISHED') return theme.colors.successBg;
    if ($status === 'PENDING_APPROVAL') return theme.colors.warningBg;
    if ($status === 'UNPUBLISHED') return theme.colors.light;
    return theme.colors.infoBg;
  }};
`;

export const ApexTagStyled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.secondary};
`;

export const ManagerCellText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.dark};

  .manager-code {
    color: ${({ theme }) => theme.colors.tertiary};
    font-size: 12px;
    margin-left: 4px;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   INLINE EDIT
   ════════════════════════════════════════════════════════════════════════════ */

export const EditCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const EditIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.tertiary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverBg};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ManagerEditWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;

  .ant-select {
    width: 100%;
  }
`;

export const ApexSwitchRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.tertiary};
  cursor: pointer;
`;

/* ════════════════════════════════════════════════════════════════════════════
   SEARCH
   ════════════════════════════════════════════════════════════════════════════ */

export const RoleReportingSearchWrapperStyled = styled.div`
  min-width: 260px;

  .ant-select {
    width: 100%;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   BULK UPDATE TAB
   ════════════════════════════════════════════════════════════════════════════ */

export const BulkToolbarStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const BulkToolbarActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const BulkTableWrapperStyled = styled.div`
  flex-grow: 1;
  overflow-y: auto;

  // A freshly-added manual row that has neither Role Reporting To nor Is
  // Apex set yet — otherwise the only signal it's incomplete is a generic
  // toast at submit time, with no per-row indication of which row it was.
  .ant-table-tbody > tr.bulk-incomplete-row > td {
    background: ${({ theme }) => theme.colors.warningBg};
  }
`;

export const BulkSubmitRowStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

// Unresolved "Reporting To" display text (couldn't be matched to a known
// role's sb_unique_id) — flags that the row will be silently excluded from
// submission unless the user re-picks a manager from the dropdown.
export const BulkUnresolvedReportingToStyled = styled.span`
  color: ${({ theme }) => theme.colors.danger};
`;
